import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, Save, UploadCloud, X } from "lucide-react";
import { importV2Service, IMPORT_V2_COLUMNS } from "../../../services/importV2.service";

const REQUIRED_COLUMNS = new Set([
  "product_sku",
  "product_name",
  "brand",
  "unit_symbol",
  "product_image_urls",
  "variant_sku",
  "variant_name",
  "variant_group",
  "variant_value",
  "pack_label",
  "mrp",
  "selling_price",
  "variant_image_urls",
]);

function pickRows(payload) {
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  return [];
}

function getJob(payload) {
  return payload?.job || payload?.data?.job || null;
}

function getJobId(payload) {
  return payload?.jobId || payload?.job?.id || payload?.data?.jobId || payload?.data?.job?.id || null;
}

function normalizeRows(rows = []) {
  return rows.map((row, index) => {
    const raw = { ...(row?.raw || {}) };
    for (const col of IMPORT_V2_COLUMNS) {
      if (raw[col] === undefined || raw[col] === null) raw[col] = "";
    }
    return {
      ...row,
      id: row?.id || "",
      rowNo: row?.rowNo ?? row?.row_no ?? index + 1,
      raw,
      errors: Array.isArray(row?.errors) ? row.errors : [],
      isValid: Boolean(row?.isValid ?? row?.is_valid),
      importStatus: row?.importStatus || row?.import_status || "pending",
      importError: row?.importError || row?.import_error || "",
    };
  });
}

function CountPill({ label, value, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-600",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };

  return (
    <div className={`rounded-xl border px-3 py-2 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] font-bold uppercase tracking-wide opacity-75">{label}</div>
      <div className="mt-1 text-[18px] font-extrabold">{value}</div>
    </div>
  );
}

function ErrorText({ row }) {
  const errors = Array.isArray(row?.errors) ? row.errors : [];
  const importError = row?.importError || "";
  if (!errors.length && !importError) {
    if (row?.importStatus === "success") return <span className="text-emerald-600">Imported</span>;
    return <span className="text-emerald-600">Ready</span>;
  }

  return (
    <div className="space-y-1 text-rose-700">
      {errors.map((err, index) => (
        <div key={`${err}-${index}`}>• {err}</div>
      ))}
      {importError ? <div>• {importError}</div> : null}
    </div>
  );
}

export default function ImportProductsModal({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [result, setResult] = useState(null);

  const counts = useMemo(() => {
    const total = rows.length;
    const valid = rows.filter((row) => row.isValid).length;
    const invalid = total - valid;
    const success = rows.filter((row) => row.importStatus === "success").length;
    const failed = rows.filter((row) => row.importStatus === "failed").length;
    const pending = rows.filter((row) => row.importStatus !== "success" && row.importStatus !== "failed").length;
    return { total, valid, invalid, success, failed, pending };
  }, [rows]);

  if (!open) return null;

  function showError(error, fallback) {
    setMessage({ type: "error", text: error?.message || fallback });
  }

  function setResponse(payload) {
    const nextJobId = getJobId(payload) || jobId;
    if (nextJobId) setJobId(nextJobId);
    setJob(getJob(payload) || null);
    setRows(normalizeRows(pickRows(payload)));
  }

  async function downloadSample() {
    try {
      setMessage({ type: "", text: "" });
      await importV2Service.downloadSample();
    } catch (e) {
      showError(e, "Sample download failed");
    }
  }

  async function uploadFile() {
    if (!file) {
      setMessage({ type: "error", text: "Please choose an Excel file first." });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      setResult(null);
      setJob(null);
      setRows([]);
      setJobId(null);

      const res = await importV2Service.uploadExcel(file);
      setResponse(res);
      setMessage({ type: "success", text: "File uploaded. Review/edit rows before final import." });
    } catch (e) {
      showError(e, "Upload failed");
    } finally {
      setLoading(false);
    }
  }

  async function saveChanges() {
    if (!jobId) {
      setMessage({ type: "error", text: "Upload file first." });
      return null;
    }

    try {
      setSaving(true);
      setMessage({ type: "", text: "" });
      const payloadRows = rows.map((row) => ({ id: row.id, rowNo: row.rowNo, raw: row.raw }));
      const res = await importV2Service.updateRows(jobId, payloadRows);
      setResponse(res);
      setMessage({ type: "success", text: "Changes saved and rows revalidated." });
      return res;
    } catch (e) {
      showError(e, "Save/revalidate failed");
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function commitImport() {
    if (!jobId) {
      setMessage({ type: "error", text: "Upload file first." });
      return;
    }

    try {
      setCommitting(true);
      setMessage({ type: "", text: "" });
      const saved = await saveChanges();
      if (!saved) return;

      const latestRows = normalizeRows(pickRows(saved));
      const validRows = latestRows.filter((row) => row.isValid);
      if (!validRows.length) {
        setMessage({ type: "error", text: "No valid rows to import. Fix errors first." });
        return;
      }

      const res = await importV2Service.commit(jobId, {});
      setResult(res);
      setResponse(res);
      const successRows = Number(res?.successRows || 0);
      const failedRows = Number(res?.failedRows || 0);
      setMessage({
        type: failedRows > 0 ? "warning" : "success",
        text: `Import finished. Success rows: ${successRows}, failed rows: ${failedRows}.`,
      });
    } catch (e) {
      showError(e, "Final import failed");
    } finally {
      setCommitting(false);
    }
  }

  function updateCell(rowIndex, column, value) {
    setRows((prev) =>
      prev.map((row, index) =>
        index === rowIndex
          ? {
              ...row,
              raw: { ...(row.raw || {}), [column]: value },
              importStatus: row.importStatus === "success" ? row.importStatus : "pending",
            }
          : row,
      ),
    );
  }

  const canClose = !loading && !saving && !committing;
  const hasImported = rows.some((row) => row.importStatus === "success");

  return (
    <div className="fixed inset-0 z-[999] flex items-end justify-center bg-black/45 p-2 sm:items-center sm:p-4">
      <div className="flex max-h-[94vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-[22px] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-[#f4d9dd] px-4 py-4 sm:px-6">
          <div>
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="text-[#d86a78]" size={22} />
              <h2 className="text-[18px] font-extrabold text-slate-800 sm:text-[22px]">Bulk Product Import V2</h2>
            </div>
            <p className="mt-1 text-[12px] leading-5 text-slate-500 sm:text-[13px]">
              Upload Excel, fix missing values in preview, then final import. Use the exact 3-level category_path: Main Category &gt; Category &gt; Subcategory (recommended). Legacy main_category + subcategory is accepted only when it resolves to a level-3 Subcategory. Existing brand/category/unit only; no auto-create.
            </p>
          </div>

          <button
            type="button"
            disabled={!canClose}
            onClick={() => {
              if (hasImported && typeof onImported === "function") return onImported();
              return onClose?.();
            }}
            className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-4 sm:px-6">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[360px_1fr]">
            <div className="space-y-4">
              <div className="rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-4">
                <button
                  type="button"
                  onClick={downloadSample}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#f2c6cc] bg-white px-4 py-3 text-[13px] font-bold text-[#d86a78] transition hover:bg-[#fff4f6]"
                >
                  <Download size={16} />
                  Download Sample Format
                </button>

                <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white p-4 text-center">
                  <UploadCloud className="mx-auto mb-2 text-slate-400" />
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="max-w-full text-[12px] text-slate-500"
                  />
                  {file ? <div className="mt-2 break-all text-[12px] font-semibold text-slate-600">{file.name}</div> : null}
                </div>

                <button
                  type="button"
                  onClick={uploadFile}
                  disabled={loading}
                  className="mt-4 w-full rounded-xl bg-[#d86a78] py-3 text-[13px] font-bold text-white transition hover:bg-[#c95b69] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Uploading..." : "Upload & Validate"}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <CountPill label="Total" value={counts.total} />
                <CountPill label="Ready" value={counts.valid} tone="emerald" />
                <CountPill label="Issues" value={counts.invalid} tone="rose" />
                <CountPill label="Imported" value={counts.success} tone="emerald" />
                <CountPill label="Failed" value={counts.failed} tone="rose" />
                <CountPill label="Pending" value={counts.pending} tone="amber" />
              </div>

              {message.text ? (
                <div
                  className={
                    "rounded-xl border px-4 py-3 text-[12px] leading-5 " +
                    (message.type === "success"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : message.type === "warning"
                        ? "border-amber-200 bg-amber-50 text-amber-700"
                        : "border-rose-200 bg-rose-50 text-rose-700")
                  }
                >
                  {message.text}
                </div>
              ) : null}

              <div className="rounded-[18px] border border-slate-200 bg-white p-4 text-[12px] leading-5 text-slate-500">
                <div className="font-bold text-slate-700">Rules</div>
                <div className="mt-2 space-y-1">
                  <p>• One row = one variant.</p>
                  <p>• Same product_sku creates multiple variants.</p>
                  <p>• Existing SKU fails; V2 is add-only for safety.</p>
                  <p>• block_mrp=yes skips the whole product group.</p>
                  <p>• Use http/https image URLs only.</p>
                </div>
              </div>
            </div>

            <div className="min-w-0 rounded-[18px] border border-[#f2c6cc] bg-white">
              <div className="flex flex-col gap-3 border-b border-[#f4d9dd] bg-[#fffafb] px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="text-[14px] font-extrabold text-slate-700">Preview & Edit</div>
                  <div className="mt-1 text-[12px] text-slate-500">
                    {jobId ? `Job: ${jobId}` : "Upload a file to start."} {job?.status ? `• Status: ${job.status}` : ""}
                  </div>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={saveChanges}
                    disabled={!jobId || saving || committing}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#f2c6cc] bg-white px-4 py-2.5 text-[12px] font-bold text-[#d86a78] transition hover:bg-[#fff4f6] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Save size={15} />
                    {saving ? "Saving..." : "Save & Revalidate"}
                  </button>

                  <button
                    type="button"
                    onClick={commitImport}
                    disabled={!jobId || committing || counts.valid <= 0}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-[12px] font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <CheckCircle2 size={15} />
                    {committing ? "Importing..." : "Final Import Valid Rows"}
                  </button>
                </div>
              </div>

              <div className="max-h-[60vh] overflow-auto">
                {!rows.length ? (
                  <div className="px-4 py-16 text-center text-[13px] text-slate-500">
                    No preview yet. Upload Excel file first.
                  </div>
                ) : (
                  <table className="min-w-[2600px] w-full text-left text-[12px]">
                    <thead className="sticky top-0 z-10 bg-[#fff4f6] text-[11px] uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-3">Row</th>
                        <th className="px-3 py-3">Status</th>
                        {IMPORT_V2_COLUMNS.map((col) => (
                          <th key={col} className="px-3 py-3">
                            <span>{col}</span>
                            {REQUIRED_COLUMNS.has(col) ? <span className="ml-1 text-rose-500">*</span> : null}
                          </th>
                        ))}
                        <th className="px-3 py-3">Errors / Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, rowIndex) => {
                        const locked = row.importStatus === "success";
                        return (
                          <tr
                            key={row.id || row.rowNo || rowIndex}
                            className={
                              "border-t border-slate-100 align-top " +
                              (locked
                                ? "bg-emerald-50/40"
                                : row.isValid
                                  ? "bg-white"
                                  : "bg-rose-50/30")
                            }
                          >
                            <td className="whitespace-nowrap px-3 py-3 font-bold text-slate-500">{row.rowNo}</td>
                            <td className="whitespace-nowrap px-3 py-3">
                              {locked ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-bold text-emerald-700">
                                  Imported
                                </span>
                              ) : row.importStatus === "failed" ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-1 text-[11px] font-bold text-rose-700">
                                  Failed
                                </span>
                              ) : row.isValid ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-bold text-emerald-700">
                                  Ready
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-1 text-[11px] font-bold text-rose-700">
                                  <AlertTriangle size={12} /> Fix
                                </span>
                              )}
                            </td>

                            {IMPORT_V2_COLUMNS.map((col) => (
                              <td key={`${row.id || rowIndex}-${col}`} className="px-2 py-2">
                                <input
                                  value={row.raw?.[col] ?? ""}
                                  disabled={locked}
                                  onChange={(e) => updateCell(rowIndex, col, e.target.value)}
                                  className={
                                    "h-9 w-full min-w-[130px] rounded-lg border px-2 text-[12px] outline-none focus:ring-2 " +
                                    (REQUIRED_COLUMNS.has(col) && !String(row.raw?.[col] || "").trim() && !locked
                                      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-rose-100"
                                      : "border-slate-200 bg-white focus:border-[#d86a78] focus:ring-[#f7d7db]") +
                                    (locked ? " cursor-not-allowed bg-emerald-50 text-slate-400" : " text-slate-600")
                                  }
                                />
                              </td>
                            ))}

                            <td className="min-w-[360px] px-3 py-3 text-[11px] leading-5">
                              <ErrorText row={row} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {result?.products?.length ? (
            <div className="mt-4 rounded-[18px] border border-slate-200 bg-white p-4">
              <div className="text-[14px] font-extrabold text-slate-700">Final Import Result</div>
              <div className="mt-3 max-h-[220px] overflow-y-auto rounded-xl border border-slate-100">
                <table className="w-full text-left text-[12px]">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Product SKU</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.products.map((item, index) => (
                      <tr key={`${item.productSku}-${index}`} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-semibold text-slate-700">{item.productSku || "-"}</td>
                        <td className="px-3 py-2 text-slate-600">{item.productName || "-"}</td>
                        <td className="px-3 py-2">
                          <span className={item.status === "success" ? "text-emerald-700" : "text-rose-700"}>{item.status}</span>
                        </td>
                        <td className="px-3 py-2 text-slate-500">{item.error || (item.variantCount ? `${item.variantCount} variants` : "-")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 border-t border-[#f4d9dd] bg-[#fffafb] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          {/* <div className="text-[12px] text-slate-500">
            Old import API is not used here. This V2 flow validates variants, images and existing masters first.
          </div> */}
          <button
            type="button"
            disabled={!canClose}
            onClick={() => {
              if (hasImported && typeof onImported === "function") return onImported();
              return onClose?.();
            }}
            className="rounded-xl border border-[#f2c6cc] bg-white px-5 py-2.5 text-[13px] font-bold text-[#d86a78] transition hover:bg-[#fff4f6] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
